import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, addDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { db, appId } from '../firebase';
import { useAuth } from '../context/AuthContext';
import MessageBox from '../components/MessageBox';
import { 
    BANKING_DETAILS, 
    ADMIN_WHATSAPP_WEBHOOK_URL_1, 
    ADMIN_WHATSAPP_WEBHOOK_URL_2 
} from '../utils/constants';

function ShopPage() {
    const { userDetails, currentUser } = useAuth();
    const navigate = useNavigate(); // Hook for redirection
    const [products, setProducts] = useState([]);
    const [myOrders, setMyOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [productToBuy, setProductToBuy] = useState(null);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('info');

    // Fetch Products and User's Orders
    useEffect(() => {
        if (!db) return;
        
        // 1. Fetch Active Products
        const productsColRef = collection(db, 'artifacts', appId, 'public', 'data', 'products');
        const qProducts = query(productsColRef, where('status', '==', 'active'));
        const unsubProducts = onSnapshot(qProducts, (snapshot) => {
            const fetchedProducts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setProducts(fetchedProducts);
            if (!currentUser) setLoading(false);
        }, (error) => {
            console.error("Error fetching products:", error);
            setLoading(false);
        });

        // 2. Fetch User Orders (only if logged in)
        let unsubOrders = () => {};
        if (currentUser) {
            const ordersColRef = collection(db, 'artifacts', appId, 'public', 'data', 'productOrders');
            const qOrders = query(ordersColRef, where('userId', '==', currentUser.uid));
            unsubOrders = onSnapshot(qOrders, (snapshot) => {
                const fetchedOrders = snapshot.docs.map(doc => ({ 
                    id: doc.id, 
                    ...doc.data(),
                    orderedAt: doc.data().orderedAt?.toDate() 
                }));
                // Sort by newest first
                fetchedOrders.sort((a,b) => b.orderedAt - a.orderedAt);
                setMyOrders(fetchedOrders);
                setLoading(false);
            }, (error) => {
                console.error("Error fetching orders:", error);
                setLoading(false);
            });
        }

        return () => {
            unsubProducts();
            unsubOrders();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser]);

    const handleBuyClick = async (product) => {
        // If not logged in, redirect to the App login page
        if (!userDetails) {
             navigate('/app');
             return;
        }
        
        setLoading(true);
        setMessage('');
        
        try {
            const ordersColRef = collection(db, 'artifacts', appId, 'public', 'data', 'productOrders');
            await addDoc(ordersColRef, {
                userId: userDetails.uid,
                userName: userDetails.displayName || userDetails.email,
                userColor: userDetails.color || '#cccccc',
                productId: product.id,
                productName: product.name,
                price: product.price,
                status: 'pending', // Pending Admin Approval/Payment
                orderedAt: new Date()
            });

            // WHATSAPP NOTIFICATION
            if (ADMIN_WHATSAPP_WEBHOOK_URL_1 || ADMIN_WHATSAPP_WEBHOOK_URL_2) {
                try {
                    const whatsappMessage = `🛍️ *New Shop Order!* \nUser: ${userDetails.displayName}\nItem: ${product.name}\nPrice: R ${product.price}`;
                    
                    const sendNotification = async (webhookUrl) => {
                        if (!webhookUrl) return;
                        const separator = webhookUrl.includes('?') ? '&' : '?';
                        const fetchUrl = `${webhookUrl}${separator}text=${encodeURIComponent(whatsappMessage)}`;
                        await fetch(fetchUrl, { mode: 'no-cors' });
                    };

                    await sendNotification(ADMIN_WHATSAPP_WEBHOOK_URL_1);
                    await sendNotification(ADMIN_WHATSAPP_WEBHOOK_URL_2); 
                    
                } catch (e) { console.warn("Notify failed", e); }
            }

            setProductToBuy(product); // Trigger Modal
        } catch (error) {
            console.error("Error ordering product:", error);
            setMessage("Failed to place order: " + error.message);
            setMessageType("error");
        } finally {
            setLoading(false);
        }
    };

    const handleCloseModal = () => {
        setProductToBuy(null);
    };

    if (loading && products.length === 0) {
            return (
            <div className="flex items-center justify-center min-h-[calc(100vh-80px)]">
                <div className="text-xl font-semibold text-gray-700">Loading Shop...</div>
            </div>
        );
    }

    return (
        <div className="py-12 container mx-auto px-6">
            <MessageBox message={message} type={messageType} onClose={() => setMessage('')} />

            {productToBuy && (
                <MessageBox
                    message={`Order placed for "${productToBuy.name}"!`}
                    onClose={handleCloseModal}
                >
                        <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                        <h4 className="text-xl font-bold text-gray-800 mb-3">Step 1: Make Payment</h4>
                        <p className="text-sm text-gray-700 mb-4">
                            Please pay <span className="font-extrabold text-green-700">R {productToBuy.price}</span>. 
                            Use your email as reference.
                        </p>
                        <div className="flex flex-col items-center">
                            <img 
                                src={BANKING_DETAILS.qrCodeUrl}
                                alt="Scan to Pay" 
                                className="w-48 h-48 object-contain rounded-lg border border-gray-300 mb-2"
                                onError={(e) => { 
                                    e.target.onerror = null; 
                                    e.target.parentElement.innerHTML = '<span class=\'text-center text-red-500 text-xs p-2\'>QR Code Error</span>'; 
                                }}
                            />
                            <p className="text-sm font-bold text-gray-600">Scan to Pay</p>
                            <div className="text-left text-sm mt-4 w-full px-4">
                                <p><strong>Bank:</strong> {BANKING_DETAILS.bank}</p>
                                <p><strong>Acc:</strong> {BANKING_DETAILS.accountNumber}</p>
                            </div>
                        </div>
                        <div className="mt-6 text-center">
                            <button onClick={handleCloseModal} className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Done</button>
                        </div>
                    </div>
                </MessageBox>
            )}

            <h1 className="font-display text-4xl md:text-5xl text-emerald-900 text-center mb-8 tracking-wider">
                Sniffspot Shop
            </h1>

            {/* Products Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {products.length === 0 ? (
                    <p className="col-span-full text-center text-gray-500 italic">No products available right now.</p>
                ) : (
                    products.map(product => (
                        <div key={product.id} className="bg-white border border-gray-200 rounded-xl shadow-lg hover:shadow-xl transition overflow-hidden flex flex-col transform hover:-translate-y-1 duration-300">
                            <div className="h-64 w-full bg-gray-100 relative">
                                {product.imageUrl ? (
                                    <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-400">No Image</div>
                                )}
                                <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full shadow font-bold text-emerald-700">
                                    R {product.price}
                                </div>
                            </div>
                            <div className="p-6 flex flex-col flex-grow">
                                <h3 className="text-xl font-bold text-gray-800 mb-2">{product.name}</h3>
                                <p className="text-gray-600 text-sm mb-6 flex-grow whitespace-pre-wrap">{product.description}</p>
                                <button 
                                    onClick={() => handleBuyClick(product)}
                                    className="w-full py-3 bg-emerald-600 text-white font-bold rounded-lg hover:bg-emerald-700 transition shadow-md"
                                >
                                    {currentUser ? 'Buy Now' : 'Login to Buy'}
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Order History (Only visible if logged in) */}
            {currentUser && myOrders.length > 0 && (
                <div className="mt-12 border-t pt-8">
                    <h3 className="text-2xl font-bold text-gray-700 mb-6">Your Order History</h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white rounded-lg shadow-sm border border-emerald-100">
                            <thead className="bg-emerald-50">
                                <tr>
                                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Product</th>
                                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Date</th>
                                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Price</th>
                                    <th className="py-3 px-4 text-left text-sm font-semibold text-gray-600">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {myOrders.map(order => (
                                    <tr key={order.id} className="border-b last:border-b-0 hover:bg-gray-50">
                                        <td className="py-3 px-4">{order.productName}</td>
                                        <td className="py-3 px-4 text-sm text-gray-500">{order.orderedAt ? order.orderedAt.toLocaleDateString() : 'N/A'}</td>
                                        <td className="py-3 px-4 font-mono">R {order.price}</td>
                                        <td className="py-3 px-4">
                                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                                order.status === 'approved' ? 'bg-green-100 text-green-800' :
                                                order.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                                'bg-yellow-100 text-yellow-800'
                                            }`}>
                                                {order.status.toUpperCase()}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ShopPage;